import type { SelectChangeEvent } from '@mui/material';
import coerceValue from 'app/common/utils/coerce-value';
import getWidgetOptions from 'app/common/utils/get-widget-options';
import { isImmutableRecord } from 'app/common/utils/immutable-type-util';
import { useCallback, useMemo } from 'react';
import type { WidgetProps } from '../../../types.js';
import { getWidgetId, useWidgetTitle } from '../../../util/widget.js';

export type EnumWidgetHookProps = {
  label: string;
  options: Array<{
    label: string;
    value: string;
  }>;
  value: string;
  handleChange: (event: SelectChangeEvent) => void;
  description?: string;
  id: string;
} & WidgetProps;

export const useEnumWidget = (props: WidgetProps) => {
  const { schema, value, onChange, onBlur } = props;

  const handleChange = useCallback(
    (event: SelectChangeEvent) => {
      onChange(coerceValue(schema, event.target.value));
      onBlur();
    },
    [onBlur, onChange, schema]
  );

  const options = useMemo(
    () =>
      getWidgetOptions(schema).map(({ label, value }) => ({
        label,
        value: String(value)
      })),
    [schema]
  );

  const label = useWidgetTitle(props);
  const id = getWidgetId(props);

  return {
    ...props,
    label,
    options,
    value: String(isImmutableRecord(value) ? value.toJS() : value),
    handleChange,
    description: schema.get('description'),
    id
  };
};
